import { useMutation } from '@tanstack/react-query';
import { Col, Form, Input, Row, Spin } from 'antd';
import dayjs from 'dayjs';
import { ReactNode, useCallback, useMemo } from 'react';

import { ApproveApi } from 'apis/ApproveApis';
import { IUpdateApprovalStatus } from 'apis/GraphQL/Variables/approve';
import {
  CheckCircleIcon,
  CustomerIcon,
  DateTimeIcon,
  InformationIcon,
  XWhite,
} from 'assets/svg';
import { Button } from 'components';
import { useUser } from 'contexts/UserProvider';
import { IUserApproval } from 'interfaces/Approve/Approve';
import { toastError, toastSuccess } from 'utils/constants/functions';
import { APPROVAL_STATUS } from '../../ApproveList';

export interface IUserApprovalInfo {
  fullName: string;
  jobTitle: string;
  dateSent: string;
  plantId: string;
  // status: string;
  status: boolean;
  note: string;
}

interface IProps {
  loading?: boolean;
  data: IUserApproval;
  title: ReactNode;
  children?: ReactNode;
  userApproval?: IUserApprovalInfo;
  refetch: () => void;
  // approvalId: string;
  // approvalStatus: string;
  // onApprove: () => void;
  // onCorrectionRequest: () => void;
}

const ApproveDetailWrapper = ({
  data,
  title,
  children,
  userApproval,
  loading = false,
  refetch,
}: IProps) => {
  const { user } = useUser();

  const isDisabled = useMemo(() => {
    return (
      data.statusApproval !== APPROVAL_STATUS.PENDING_APPROVAL ||
      data?.tuserApprovalDetail?.find((item) => item?.email === user?.email)
        ?.approvalYn !== null
    );
  }, [data, user]);

  const { mutate: updateApprovalStatus } = useMutation({
    mutationFn: (params: IUpdateApprovalStatus) =>
      ApproveApi.updateApprovalStatus(params),
    onSuccess: (res: any) => {
      if (res.errors?.length) {
        toastError(res.errors?.[0]?.message || 'Có lỗi xảy ra');
        return;
      }

      toastSuccess('Thành công');
      refetch();
    },
    onError: (error) => {
      console.log(error);
      toastError(`Phê duyệt thất bại: ${error.message}`);
    },
  });

  const handleApprove = useCallback(() => {
    if (!data.tuserApprovalDetail.length) {
      toastError('Không tim thấy thông tin phê duyệt!');
      return;
    }

    const userApprovalDetail =
      user?.isAdmin || user?.isHO
        ? data.tuserApprovalDetail?.filter(
            (item) => item?.email === user?.email,
          )
        : data.tuserApprovalDetail;

    updateApprovalStatus({
      id: userApprovalDetail?.[0]?.id,
      approvalYn: true,
    });
  }, [data, user]);

  const handleTableCorrectionRequest = useCallback(() => {
    if (!data.tuserApprovalDetail.length) {
      toastError('Không tim thấy thông tin phê duyệt!');
      return;
    }

    const userApprovalDetail =
      user?.isAdmin || user?.isHO
        ? data.tuserApprovalDetail?.filter(
            (item) => item?.email === user?.email,
          )
        : data.tuserApprovalDetail;

    updateApprovalStatus({
      id: userApprovalDetail?.[0]?.id,
      approvalYn: false,
    });
  }, [data, user]);

  // const handleExportExcel = () => {
  //   console.log('export');
  // };

  return (
    <Spin spinning={loading}>
      <Row gutter={[8, 16]} align="middle" justify="end">
        {/* <Col>
          <ButtonExcel onClick={handleExportExcel} />
        </Col> */}
        <Col>
          <Button
            displayType="error"
            title="YC hiệu chỉnh"
            iconBefore={<XWhite />}
            disabled={isDisabled}
            handleOnclick={handleTableCorrectionRequest}
            // disabled={data.statusApproval !== APPROVAL_STATUS.PENDING_APPROVAL}
            // disabled={approvalStatus !== APPROVAL_STATUS.PENDING_APPROVAL}
            // handleOnclick={onCorrectionRequest}
          />
        </Col>
        <Col>
          <Button
            title="Duyệt"
            displayType="approve"
            iconBefore={<CheckCircleIcon />}
            disabled={isDisabled}
            handleOnclick={handleApprove}
            // disabled={data.statusApproval !== APPROVAL_STATUS.PENDING_APPROVAL}
            // disabled={approvalStatus !== APPROVAL_STATUS.PENDING_APPROVAL}
            // handleOnclick={() => onApprove()}
          />
        </Col>
      </Row>
      <div className="flex bg-white w-max px-4 py-[2px] rounded-full gap-x-1">
        <span>Mã phê duyệt:</span>
        {/* <span className="font-bold text-[#0077CC]">{approvalId}</span> */}
        <span className="font-bold text-[#0077CC]">{data.id}</span>
      </div>
      <Row gutter={16} align="middle" justify="space-between" className="mt-7">
        <Col span={8}>
          <div className="bg-white rounded-lg w-full h-[100px] flex items-center gap-x-2 sm:gap-x-3 lg:gap-x-4 justify-start py-4 px-6">
            <CustomerIcon className="size-12" />
            <Row gutter={[5, 0]}>
              <Col>
                <Row>
                  <p className="text-sm">Người gửi:</p>
                </Row>
                <Row>
                  <p className="text-sm">Chức vụ:</p>
                </Row>
              </Col>
              <Col>
                <Row>
                  <p className="font-semibold text-sm">
                    {userApproval?.fullName ?? '-'}
                  </p>
                </Row>
                <Row>
                  <p className="text-sm">{userApproval?.jobTitle ?? '-'}</p>
                </Row>
              </Col>
            </Row>
          </div>
        </Col>
        <Col span={8}>
          <div className="bg-white rounded-lg w-full h-[100px] flex items-center gap-x-2 sm:gap-x-3 lg:gap-x-4 justify-start py-4 px-6">
            <DateTimeIcon className="size-12" />
            <Row gutter={[5, 0]}>
              <Col>
                <Row>
                  <p className="text-sm">Ngày gửi:</p>
                </Row>
                <Row>
                  <p className="text-sm">Plant:</p>
                </Row>
              </Col>
              <Col>
                <Row>
                  <p className="text-sm">
                    {dayjs(userApproval?.dateSent).format('HH:mm DD/MM/YYYY') ??
                      '-'}
                  </p>
                </Row>
                <Row>
                  <p className="text-sm">{userApproval?.plantId ?? '-'}</p>
                </Row>
              </Col>
            </Row>
          </div>
        </Col>
        <Col span={8}>
          <div className="bg-white rounded-lg w-full h-[100px] flex items-center gap-x-2 sm:gap-x-3 lg:gap-x-4 justify-start py-4 px-6">
            <InformationIcon className="size-12" />
            <Row gutter={[5, 0]}>
              <Col>
                <Row>
                  <p className="text-sm">Trạng thái:</p>
                </Row>
                {/* <Row>
                  <p className="text-sm">Lịch sử phê duyệt:</p>
                </Row> */}
              </Col>
              <Col>
                <Row>
                  <p className="text-sm">
                    {userApproval?.status === true
                      ? 'Đã phê duyệt'
                      : userApproval?.status === false
                        ? 'Từ chối'
                        : '	Chờ phê duyệt'}
                  </p>
                </Row>
                {/* <Row>
                  <p
                    // href="#"
                    className="text-[#0077CC] font-semibold no-underline text-sm"
                  >
                    Xem chi tiết
                  </p>
                </Row> */}
              </Col>
            </Row>
          </div>
        </Col>
      </Row>
      <Form className="mt-7">
        <Form.Item
          name="note"
          rules={[{ required: true, message: 'Vui lòng nhập ghi chú!' }]}
        >
          <Input.TextArea
            className="pt-[18px] pl-6 pb-9 rounded-lg"
            placeholder="Nhập ghi chú tại đây..."
          />
        </Form.Item>
      </Form>
      <section className="p-7 bg-[#FFFFFF] rounded-lg w-full">
        <Row>
          <Col>
            <p className="text-xl font-bold text-[#003056]">{title}</p>
          </Col>
        </Row>
        {children}
      </section>
    </Spin>
  );
};

export default ApproveDetailWrapper;
